import { FC, useEffect } from 'react';

import {
  DevHubBox,
  DevHubCard,
  DevHubGrid,
  DevHubList,
  DevHubPaper,
} from '@devxdiscover/devhub-ui';

import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';

import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';

import {
  DefaultResultListItem,
  SearchBar,
  SearchFilter,
  SearchResult,
  useSearch,
} from '@backstage/plugin-search-react';
import { Content, Header, Page } from '@backstage/core-components';
import { SearchSourcesAccordion } from './components/SearchSources';
import { SearchTypesAccordion } from './components/SearchTypes';
import { Chip } from '@material-ui/core';
import DevHubSupportButton from '../../custom/components/common/DevHubSupportButton';

type SearchHighlightChipsProps = {
  type?: string;
  kind?: string;
  source?: string;
};

const EnterpriseSearchPage = () => {
  const { filters, setFilters } = useSearch();
  // const [selectedButton, setSelectedButton] = React.useState('allSources');

  useEffect(() => {
    setFilters({ source: 'All' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const SearchHighlightChips: FC<{
    document: SearchHighlightChipsProps;
  }> = ({ document }) => {
    return (
      <>
        {document?.type && (
          <Chip label={`Type: ${document.type}`} size="small" />
        )}
        {document?.source && (
          <Chip label={`Source: ${document.source}`} size="small" />
        )}
      </>
    );
  };

  return (
    <Page themeId="home">
      <Header
        title="Search"
        subtitle="Seamlessly search internal and external sources"
      >
        <DevHubBox display="flex" justifyContent="flex-end">
          <DevHubSupportButton color="white">Support</DevHubSupportButton>
        </DevHubBox>
      </Header>
      <Content>
        <DevHubGrid container direction="row">
          <DevHubGrid item xs={12}>
            <DevHubPaper>
              <SearchBar />
            </DevHubPaper>
          </DevHubGrid>
          <DevHubGrid item xs={3}>
            <SearchSourcesAccordion name="Source" />
            {filters.source === 'DevHub' && (
              <>
                <DevHubPaper>
                  <SearchTypesAccordion name="Filter by Type" />
                </DevHubPaper>
                <DevHubPaper>
                  <SearchFilter.Select
                    label="kind"
                    name="kind"
                    values={['API', 'Template', 'Application', 'Component']}
                  />
                </DevHubPaper>
              </>
            )}
          </DevHubGrid>

          <DevHubGrid item xs={9}>
            <SearchResult>
              {({ results }) => (
                <>
                  <DevHubList>
                    {results.map(({ type, document, highlight, rank }) => {
                      const seondaryActionProps = {
                        type: type,
                      };
                      switch (type) {
                        case 'software-catalog':
                          return (
                            <DevHubCard
                              body={
                                <CatalogSearchResultListItem
                                  key={document.location}
                                  result={document}
                                  highlight={highlight}
                                  rank={rank}
                                />
                              }
                            />
                          );
                        case 'techdocs':
                          return (
                            <DevHubCard
                              body={
                                <TechDocsSearchResultListItem
                                  title={document.title}
                                  key={document.location}
                                  result={document}
                                  highlight={highlight}
                                  rank={rank}
                                />
                              }
                            />
                          );
                        default:
                          return (
                            <DevHubCard
                              body={
                                <DefaultResultListItem
                                  key={document.location}
                                  result={document}
                                  highlight={highlight}
                                  rank={rank}
                                  secondaryAction={
                                    <SearchHighlightChips
                                      document={seondaryActionProps}
                                    />
                                  }
                                />
                              }
                            />
                          );
                      }
                    })}
                  </DevHubList>
                </>
              )}
            </SearchResult>
          </DevHubGrid>
        </DevHubGrid>
      </Content>
    </Page>
  );
};

/**
 * The search page component for rendering the search page.
 */
export const EnterpriseSearch = <EnterpriseSearchPage />;
